import { ACTIVATION_LABELS, resolveSupportLabel } from '../classification';
import { FOCUSED_REASON_LABELS } from '../bubble/ancestryChips';
import { volumeDisplayName } from '../formatBytes';
import { resolveAccountDrillPrefix } from '../sidebar/accountDrillPrefix';
import { reasonKeyForVolume, reasonLabelForVolume } from '../sidebar/focusedReasonHelpers';
import { emptyDrillLocation, structuralPathStrings } from '../drill/drillLocation';
import { resolveDrillScope } from '../drill/resolveDrillScope';
import { traceExplorationCanvas } from '../explorationCanvasTrace';
import { effectiveSecondary } from '../secondaryGrouping';
import { getGroupingModel } from '../grouping';
import type {
  DrillLocation,
  HierarchyNode,
  ExplorationVolume,
  ViewMode,
} from '../../types/exploration';
import {
  accountKey,
  bucketExplorationTopGroups,
  type ExplorationTopGroup,
} from './buildClusterTopLevel';

type HierarchyBuilderNode = {
  name: string;
  type: string;
  key?: string;
  kind?: string;
  value?: number;
  count?: number;
  _volumes?: ExplorationVolume[];
  volume?: ExplorationVolume;
  children?: HierarchyBuilderNode[] | null;
};

type TopGroupNode = ExplorationTopGroup & HierarchyBuilderNode;

type StatusBucket = HierarchyBuilderNode & {
  type: 'status';
  key: string;
  _volumes: ExplorationVolume[];
  children?: HierarchyBuilderNode[];
};

type ReasonBucket = HierarchyBuilderNode & {
  type: 'reason';
  key: string;
  _volumes: ExplorationVolume[];
  children?: HierarchyBuilderNode[];
};

type VolumeLeaf = HierarchyBuilderNode & {
  type: 'volume';
  volume: ExplorationVolume;
};

function volumeLeaf(v: ExplorationVolume): VolumeLeaf {
  return {
    name: volumeDisplayName(v),
    type: 'volume',
    volume: v,
    value: v.size || 1,
  };
}

export interface BuildHierarchyOptions {
  innerKey: 'supportStatus' | 'activationStatus';
  secondary: 'cluster' | 'region' | 'tag' | 'none';
  tagKey?: string;
  location?: DrillLocation;
  groupByAccount?: boolean;
}

type NormalizedBuildHierarchyOptions = Omit<BuildHierarchyOptions, 'location'> & {
  drillPath: string[];
  location: DrillLocation;
};

function statusDrillWrapper(
  label: string,
  inner: ExplorationVolume[],
  innerKey: BuildHierarchyOptions['innerKey'],
  nodeType: 'status' | 'reason' = 'status',
  keyOverride?: string
): HierarchyBuilderNode {
  const innerKeyVal = keyOverride ?? inner[0][innerKey];
  return {
    name: label,
    key: innerKeyVal,
    type: nodeType,
    _volumes: inner,
    value: inner.reduce((s, v) => s + (v.size || 0), 0),
    count: inner.length,
    children: inner.map((v) => ({
      name: volumeDisplayName(v),
      type: 'volume',
      volume: v,
      value: v.size || 1,
    })),
  };
}

/**
 * Reason breakdown within a focused Unsupported/Pending-setup category.
 * Pending setup buckets by the raw datafiableReasons (why activation hasn't
 * happened yet); Unsupported buckets by supportStatus (why it isn't
 * datafiable at all).
 */
function reasonBucketChildren(
  volumes: ExplorationVolume[],
  focusedKey: string
): HierarchyBuilderNode[] {
  const m = new Map<string, HierarchyBuilderNode & { _volumes: ExplorationVolume[] }>();
  volumes.forEach((v) => {
    const k = focusedKey === 'potentiallySupported' ? reasonKeyForVolume(v, focusedKey) : v.supportStatus;
    const label =
      focusedKey === 'potentiallySupported'
        ? reasonLabelForVolume(v, focusedKey)
        : resolveSupportLabel(k || '');
    if (!m.has(k)) m.set(k, { name: label, key: k, type: 'reason', _volumes: [] });
    m.get(k)!._volumes.push(v);
  });
  return [...m.values()]
    .map((g) => ({
      ...g,
      value: g._volumes.reduce((s, v) => s + (v.size || 0), 0),
      count: g._volumes.length,
    }))
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
}

/** True when the user explicitly drilled into this node and volume leaves may render. */
function isVolumeDrillTarget(node: HierarchyBuilderNode, opts: NormalizedBuildHierarchyOptions): boolean {
  if (node.name == null) return false;

  const drillPath = opts.drillPath || [];
  if (!drillPath.length) return false;

  // status/reason nodes are never the drill target here — resolveDrillScope
  // already short-circuits (see buildExplorationHierarchy) whenever a
  // status, focused-activation, or reason facet is set on the current
  // location, so by the time this runs drillPath is purely structural.
  if (node.type === 'instance') {
    return drillPath[drillPath.length - 1] === node.name;
  }

  return false;
}

export function buildExplorationHierarchy(
  volumes: ExplorationVolume[],
  opts: BuildHierarchyOptions
): HierarchyNode {
  const location = opts.location ?? emptyDrillLocation();
  const view: ViewMode = opts.innerKey === 'supportStatus' ? 'exploration' : 'activation';
  const resolvedScope = resolveDrillScope(volumes, {
    secondary: opts.secondary,
    location,
    groupByAccount: opts.groupByAccount ?? false,
    tagKey: opts.tagKey ?? '',
    view,
  });
  const normalizedOpts: NormalizedBuildHierarchyOptions = {
    ...opts,
    location,
    // Impl's own account-wrapper branch expects the account as drillPath[0]
    // (it strips it itself via resolveAccountDrillPrefix) — unlike
    // resolvedScope.structuralPath, which is deliberately account-free for
    // depth-based consumers (see ResolvedDrillScope).
    drillPath: structuralPathStrings(location, { groupByAccount: opts.groupByAccount ?? false }),
  };
  const innerKey = normalizedOpts.innerKey;

  if (resolvedScope.statusLabel && resolvedScope.volumes.length > 0) {
    const wrapper = statusDrillWrapper(resolvedScope.statusLabel, resolvedScope.volumes, innerKey);
    return { name: 'All', type: 'root', children: [wrapper] } as HierarchyNode;
  }

  // A focused Unsupported/Pending-setup category, with no specific reason
  // chosen yet — show the reason breakdown (regardless of whether we're also
  // inside a structural cluster/region/tag context, or at the top under
  // `none` grouping, which has no structural context at all). Unsupported
  // has no reason breakdown at all — it goes straight to its volumes.
  if (
    resolvedScope.focusedKey &&
    resolvedScope.volumes.length > 0 &&
    !resolvedScope.reasonLabel
  ) {
    const label =
      ACTIVATION_LABELS[resolvedScope.focusedKey as keyof typeof ACTIVATION_LABELS] ??
      resolvedScope.focusedKey;
    const children: HierarchyBuilderNode[] =
      resolvedScope.focusedKey === 'unsupported'
        ? resolvedScope.volumes.map(volumeLeaf)
        : reasonBucketChildren(resolvedScope.volumes, resolvedScope.focusedKey);
    const wrapper: HierarchyBuilderNode = {
      name: label,
      key: resolvedScope.focusedKey,
      type: 'status',
      _volumes: resolvedScope.volumes,
      children,
      value: resolvedScope.volumes.reduce((s, v) => s + (v.size || 0), 0),
      count: resolvedScope.volumes.length,
    };
    return { name: 'All', type: 'root', children: [wrapper] } as HierarchyNode;
  }

  if (resolvedScope.reasonLabel && resolvedScope.volumes.length > 0) {
    const reasonKey = reasonKeyForVolume(resolvedScope.volumes[0]!, resolvedScope.focusedKey ?? '');
    const wrapper = {
      name: resolvedScope.reasonLabel,
      key: reasonKey,
      type: 'reason' as const,
      _volumes: resolvedScope.volumes,
      value: resolvedScope.volumes.reduce((s, v) => s + (v.size || 0), 0),
      count: resolvedScope.volumes.length,
      children: resolvedScope.volumes.map((v) => ({
        name: volumeDisplayName(v),
        type: 'volume' as const,
        volume: v,
        value: v.size || 1,
      })),
    };
    return { name: 'All', type: 'root', children: [wrapper] } as HierarchyNode;
  }

  const tree = _buildExplorationHierarchyImpl(volumes, normalizedOpts);
  return (applyShallowDepth(tree, normalizedOpts, volumes) ?? tree) as HierarchyNode;
}

// Cap the visible tree to at most 2 non-free, non-volume levels under the drill
// scope. account is always free; a node that's the only child at its level is
// also "free" (single-value-collapsed). Volumes are stripped unless the parent
// is the drill target (status or instance the user explicitly drilled into).
// `_volumes` on each node still powers size/count chips and tooltips.
function applyShallowDepth(
  root: HierarchyBuilderNode | null | undefined,
  opts: NormalizedBuildHierarchyOptions,
  volumes: ExplorationVolume[]
): HierarchyBuilderNode | null | undefined {
  if (!root || !root.children) return root;

  // We walk children; per-node we know:
  //   nonFreeDepth = how many "counted" levels we've descended past
  //   isOnlyChild = parent had a single child (so this level collapses)
  function walk(
    node: HierarchyBuilderNode,
    nonFreeDepth: number,
    parentOnlyChild: boolean
  ): void {
    if (!node || !node.children || !node.children.length) return;
    const kids = node.children;
    // Determine if this node counts toward the budget.
    // account is always free. The structural root isn't a "level" itself.
    const isAccount = node.type === 'account';
    const isRoot    = node.type === 'root';
    const counts    = !isRoot && !isAccount && !parentOnlyChild;
    const myDepth   = counts ? nonFreeDepth + 1 : nonFreeDepth;
    const onlyChild = kids.length === 1;

    // If we've already used the visible budget (2 non-free levels), strip
    // children that aren't a continuation into volumes-of-a-drilled-target.
    // Volumes only render when their parent is the explicit drill target —
    // anywhere else (top view, intermediate drill levels), strip them.
    // Use null (not []) so d3.hierarchy treats the parent as a leaf and the
    // .sum() accessor reads `data.value` instead of summing 0.
    const childrenAreVolumes = kids.length > 0 && kids[0].type === 'volume';
    const isDrillTarget = isVolumeDrillTarget(node, opts);
    if (childrenAreVolumes && !isDrillTarget) {
      // Special case: an instance with volume children. We don't want an empty
      // instance bubble — regroup the instance's volumes by support status so
      // the shallow view still shows colored status sub-bubbles inside each
      // instance.
      if (node.type === 'instance' && Array.isArray(node._volumes) && node._volumes.length) {
        const innerKey = opts.innerKey;
        const labelOf = (v: ExplorationVolume) =>
          v[innerKey === 'supportStatus' ? 'supportLabel' : 'activationLabel'];
        const buckets = new Map<string, StatusBucket>();
        node._volumes.forEach(v => {
          const k = v[innerKey];
          if (!buckets.has(k)) buckets.set(k, { name: labelOf(v), key: k, type: 'status', _volumes: [] });
          buckets.get(k)!._volumes.push(v);
        });
        node.children = [...buckets.values()]
          .map(g => ({
            ...g,
            value: g._volumes.reduce((s, v) => s + (v.size || 0), 0),
            count: g._volumes.length,
            children: null,    // shallow: don't render the volume leaves
          }))
          .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
        return;
      }
      node.children = null;
      return;
    }
    // Depth cap: at 2 non-free visible levels we stop expanding deeper groupings.
    if (myDepth >= 2 && !childrenAreVolumes) {
      node.children = null;
      return;
    }
    // Recurse into each child, telling it whether it's an only child.
    kids.forEach(k => walk(k, myDepth, onlyChild));
  }
  walk(root, 0, false);
  return root;
}

// ── (shallow build) Group:None tree builders ────────────────────────────────
function _buildStatusOnlyRoot(
  volumes: ExplorationVolume[],
  innerKey: BuildHierarchyOptions['innerKey'],
  drillPath: string[],
  opts: Pick<BuildHierarchyOptions, 'secondary' | 'groupByAccount' | 'tagKey'>
): HierarchyBuilderNode {
  const innerLabelOf = (v: ExplorationVolume) =>
    v[innerKey === 'supportStatus' ? 'supportLabel' : 'activationLabel'];
  const groups = new Map<string, StatusBucket>();
  volumes.forEach(v => {
    const k = v[innerKey];
    if (!groups.has(k)) groups.set(k, { name: innerLabelOf(v), key: k, type: 'status', _volumes: [] });
    groups.get(k)!._volumes.push(v);
  });
  const built = [...groups.values()].map(g => {
    g.value = g._volumes.reduce((s, v) => s + (v.size || 0), 0);
    g.count = g._volumes.length;
    g.children = g._volumes.map(volumeLeaf);
    return g;
  }).sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  // Callers only reach here when drillPath is empty — a status/reason drill
  // already short-circuits in buildExplorationHierarchy before this
  // function runs (see resolvedScope.statusLabel/focusedKey there).
  return { name: 'All', type: 'root', children: built };
}
function _buildAccountStatusRoot(
  volumes: ExplorationVolume[],
  innerKey: BuildHierarchyOptions['innerKey'],
  drillPath: string[],
  opts: Pick<BuildHierarchyOptions, 'secondary' | 'groupByAccount' | 'tagKey'>
): HierarchyBuilderNode {
  const accountKeyFn = (v: ExplorationVolume) =>
    v.accountName || v.accountId || '(unknown account)';
  const accountChildren: HierarchyBuilderNode[] = [];

  const byAccount = new Map<string, ExplorationVolume[]>();
  volumes.forEach(v => {
    const k = accountKeyFn(v);
    if (!byAccount.has(k)) byAccount.set(k, []);
    byAccount.get(k)!.push(v);
  });
  byAccount.forEach((vols, name) => {
    const inner = _buildStatusOnlyRoot(vols, innerKey, drillPath.slice(1), opts);
    accountChildren.push({
      name, type: 'account', kind: 'account', _volumes: vols,
      value: vols.reduce((s, v) => s + (v.size || 0), 0),
      count: vols.length,
      children: inner.children,
    });
  });
  accountChildren.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  if (drillPath.length > 0) {
    const { accountName } = resolveAccountDrillPrefix(drillPath, true, volumes);
    if (accountName) {
      const sel = accountChildren.find(a => a.name === accountName);
      if (sel) return { name: 'All', type: 'root', children: [sel] };
    } else {
      const { path: withoutAccount } = resolveAccountDrillPrefix(drillPath, true, volumes);
      return _buildStatusOnlyRoot(
        volumes,
        innerKey,
        withoutAccount.length ? withoutAccount : drillPath,
        opts
      );
    }
  }
  return { name: 'All', type: 'root', children: accountChildren };
}

/**
 * Under 'none' grouping the structural facet is always empty unless an
 * instance is specifically focused (which borrows the cluster shape — see
 * `noneGrouping`), so a non-empty structural path here can only mean an
 * instance drill.
 */
function isNoneGroupingInstanceDrill(drillPath: string[]): boolean {
  return drillPath.length > 0;
}

function _buildExplorationHierarchyImpl(
  volumes: ExplorationVolume[],
  opts: NormalizedBuildHierarchyOptions
): HierarchyBuilderNode {
  let { innerKey, secondary, tagKey, drillPath = [] } = opts;
  secondary = effectiveSecondary(secondary, tagKey ?? '');
  let hierarchySecondary = secondary;

  // (shallow build) Group:None — treat as if the top level is the status itself.
  // Instance drill paths reuse the infrastructure bucket model.
  if (secondary === 'none' && !opts.groupByAccount) {
    if (!isNoneGroupingInstanceDrill(drillPath)) {
      return _buildStatusOnlyRoot(volumes, innerKey, drillPath || [], opts);
    }
    hierarchySecondary = 'cluster';
  }
  if (secondary === 'none' && opts.groupByAccount) {
    if (!isNoneGroupingInstanceDrill(drillPath)) {
      return _buildAccountStatusRoot(volumes, innerKey, drillPath || [], opts);
    }
    hierarchySecondary = 'cluster';
  }

  // A focused Unsupported/Pending-setup drill (with or without a specific
  // reason chosen) is a DrillLocation field (focusedActivationKey/reasonLabel),
  // not a trailing drillPath element — buildExplorationHierarchy
  // already short-circuits those cases (via resolvedScope) before this
  // function ever runs, so drillPath here is always purely structural.
  const focusedReasonTail: string | null = null;
  const useFocusedReasonWrapper = false;

  const innerLabelOf = (v: ExplorationVolume) =>
    v[innerKey === 'supportStatus' ? 'supportLabel' : 'activationLabel'];

  function leafChildren(vols: ExplorationVolume[]): VolumeLeaf[] {
    return vols.map(volumeLeaf);
  }
  function buildStatusChildren(vols: ExplorationVolume[]): StatusBucket[] {
    const m = new Map<string, StatusBucket>();
    vols.forEach(v => {
      const k = v[innerKey];
      if (!m.has(k)) m.set(k, { name: innerLabelOf(v), key: k, type: 'status', _volumes: [], children: [] });
      m.get(k)!._volumes.push(v);
    });
    m.forEach(g => {
      g.children = leafChildren(g._volumes);
      g.value = g._volumes.reduce((s, v) => s + (v.size || 0), 0);
      g.count = g._volumes.length;
    });
    return [...m.values()].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  }
  // (shallow build) "Pending setup" reason breakdown — buckets by the RAW
  // datafiableReasons (why hasn't this been activated yet: no autoscaler,
  // unmounted, etc.), not by supportStatus.
  function buildActivationReasonChildren(vols: ExplorationVolume[]): ReasonBucket[] {
    const m = new Map<string, ReasonBucket>();
    vols.forEach(v => {
      const k = reasonKeyForVolume(v, 'potentiallySupported');
      const label = reasonLabelForVolume(v, 'potentiallySupported');
      if (!m.has(k)) m.set(k, { name: label, key: k, type: 'reason', _volumes: [], children: [] });
      m.get(k)!._volumes.push(v);
    });
    m.forEach(g => {
      g.children = leafChildren(g._volumes);
      g.value = g._volumes.reduce((s, v) => s + (v.size || 0), 0);
      g.count = g._volumes.length;
    });
    return [...m.values()].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  }
  // At the instance level we skip the status grouping — volumes sit directly
  // under an instance and carry a small status badge in the corner instead.
  function buildInstanceChildren(vols: ExplorationVolume[]): HierarchyBuilderNode[] {
    const m = new Map<string, HierarchyBuilderNode & { _volumes: ExplorationVolume[]; children: VolumeLeaf[] }>();
    vols.forEach(v => {
      const k = v.instanceId || '(no instance)';
      if (!m.has(k)) m.set(k, { name: k, type: 'instance', _volumes: [], children: [] });
      m.get(k)!._volumes.push(v);
    });
    m.forEach(g => {
      g.children = leafChildren(g._volumes);
      g.value = g._volumes.reduce((s, v) => s + (v.size || 0), 0);
      g.count = g._volumes.length;
    });
    return [...m.values()].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  }
  function buildNodeGroupChildren(vols: ExplorationVolume[]): HierarchyBuilderNode[] {
    const m = new Map<string, HierarchyBuilderNode & { _volumes: ExplorationVolume[]; children: StatusBucket[] }>();
    vols.forEach(v => {
      const k = v.nodeGroup || '(No Group)';
      if (!m.has(k)) m.set(k, { name: k, type: 'nodeGroup', _volumes: [], children: [] });
      m.get(k)!._volumes.push(v);
    });
    m.forEach(g => {
      g.children = buildStatusChildren(g._volumes);
      g.value = g._volumes.reduce((s, v) => s + (v.size || 0), 0);
      g.count = g._volumes.length;
    });
    return [...m.values()].sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
  }
  function buildNodeGroupInstanceDrillRoot(
    drillVolumes: ExplorationVolume[],
    rest: string[]
  ): HierarchyBuilderNode {
    const ngVols = drillVolumes.filter((v) => (v.nodeGroup || '(No Group)') === rest[0]);
    const ng: HierarchyBuilderNode = {
      name: rest[0], type: 'nodeGroup', _volumes: ngVols,
      value: ngVols.reduce((s, v) => s + (v.size || 0), 0),
      count: ngVols.length,
    };
    if (rest.length === 1) {
      ng.children = buildInstanceChildren(ngVols);
      return { name: 'All', type: 'root', children: [ng] };
    }
    const instVols = ngVols.filter(v => (v.instanceId || '(no instance)') === rest[1]);
    if (instVols.length === 0) {
      traceExplorationCanvas('hierarchy empty instance drill', {
        kind: 'nodeGroup',
        drillPath,
        instanceId: rest[1],
      });
      return { name: 'All', type: 'root', children: [] };
    }
    const inst: HierarchyBuilderNode = {
      name: rest[1], type: 'instance', _volumes: instVols,
      value: instVols.reduce((s, v) => s + (v.size || 0), 0),
      count: instVols.length,
      children: buildStatusChildren(instVols),
    };
    return { name: 'All', type: 'root', children: [inst] };
  }

  // ── Optional Account wrapper (org view only) — sits ABOVE the existing
  //    cluster/region/tag grouping. drillPath's first segment is the account
  //    name when groupByAccount is on; the rest is the regular structural
  //    drill within that account. ──
  if (opts.groupByAccount) {
    if (drillPath.length === 0) {
      const top: HierarchyBuilderNode[] = [];
      const buckets = new Map<string, ExplorationVolume[]>();
      volumes.forEach(v => {
        const k = accountKey(v);
        if (!buckets.has(k)) buckets.set(k, []);
        buckets.get(k)!.push(v);
      });
      buckets.forEach((vols, name) => {
        const inner = buildExplorationHierarchy(vols, {
          innerKey, secondary, tagKey, location: emptyDrillLocation(), groupByAccount: false,
        });
        top.push({
          name, type: 'account', kind: 'account', _volumes: vols,
          value: vols.reduce((s, v) => s + (v.size || 0), 0),
          count: vols.length,
          children: inner.children,
        });
      });
      top.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
      return { name: 'All', type: 'root', children: top };
    }
    
    const accountName = opts.location.account ?? null;
    const restPath = drillPath.slice(1);
    if (!accountName) {
      return buildExplorationHierarchy(volumes, {
        innerKey, secondary, tagKey,
        location: { ...opts.location, account: undefined },
        groupByAccount: false,
      });
    }
    const accountVolumes = volumes.filter(v => accountKey(v) === accountName);
    if (restPath.length === 0) {
      // Just drilled into account: show it as a wrapper containing the
      // current grouping (cluster/region/tag).
      const inner = buildExplorationHierarchy(accountVolumes, {
        innerKey, secondary, tagKey, location: emptyDrillLocation(), groupByAccount: false,
      });
      return { name: 'All', type: 'root', children: [{
        name: accountName, type: 'account', kind: 'account',
        _volumes: accountVolumes,
        value: accountVolumes.reduce((s, v) => s + (v.size || 0), 0),
        count: accountVolumes.length,
        children: inner.children,
      }] };
    }
    // Drilled deeper than account — defer to the regular hierarchy scoped to
    // that account. The account wrapper peels away (consistent with how
    // cluster wrappers peel when drilling deeper into them).
    return buildExplorationHierarchy(accountVolumes, {
      innerKey, secondary, tagKey,
      location: { ...opts.location, account: undefined },
      groupByAccount: false,
    });
  }

  // ── Bucket into top-level groups ──
  // For 'cluster' mode: K8s clusters are separate groups; EC2 (instanceId, no cluster) and
  // Unattached (no instance) are their own top-level groups too.
  // For 'region' / 'tag' modes: just bucket by that key.
  const topGroups = bucketExplorationTopGroups(volumes, hierarchySecondary, tagKey) as TopGroupNode[];

  // Default tree (no drill): every group → status → volumes.
  function attachStatusOnly(g: TopGroupNode): TopGroupNode {
    g.children = buildStatusChildren(g._volumes);
    g.value = g._volumes.reduce((s, v) => s + (v.size || 0), 0);
    g.count = g._volumes.length;
    return g;
  }

  if (drillPath.length === 0) {
    const built = topGroups.map(g => attachStatusOnly(g));
    built.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    return { name: 'All', type: 'root', children: built };
  }

  // ── Drilled — show only the matched top-level group, expanded ──
  const top = topGroups.find(g => g.name === drillPath[0]);
  if (!top) {
    // Stale drill path — fall back to default
    const built = topGroups.map(g => attachStatusOnly(g));
    built.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    return { name: 'All', type: 'root', children: built };
  }

  top.value = top._volumes.reduce((s, v) => s + (v.size || 0), 0);
  top.count = top._volumes.length;

  const rest = drillPath.slice(1);

  // Drill model: each level deeper REPLACES the visible root with the drilled
  // bubble. So drilling into a node group hides the cluster wrapper; drilling
  // into an instance hides the node group wrapper. The breadcrumb/side panel
  // still track the full path.
  if (top.kind === 'flat') {
    if (rest.length > 0) {
      const instanceId = rest[0];
      const instVols = top._volumes.filter((v) => (v.instanceId || '(no instance)') === instanceId);
      if (instVols.length > 0) {
        const inst: HierarchyBuilderNode = {
          name: instanceId,
          type: 'instance',
          _volumes: instVols,
          value: instVols.reduce((s, v) => s + (v.size || 0), 0),
          count: instVols.length,
          children: buildStatusChildren(instVols),
        };
        return { name: 'All', type: 'root', children: [inst] };
      }
    }
    attachStatusOnly(top);
    return { name: 'All', type: 'root', children: [top] };
  }

  if (top.kind === 'cluster') {
    if (rest.length === 0) {
      top.children = buildNodeGroupChildren(top._volumes);
      return { name: 'All', type: 'root', children: [top] };
    }
    return buildNodeGroupInstanceDrillRoot(top._volumes, rest);
  }

  if (top.kind === 'ec2') {
    // Both views list instances under the EC2 top bubble (matching
    // cluster → node group), same as the sidebar breakdown bars already do
    // (computeTopLevelGroups.ts) — drilling further into an instance still
    // shows its activation/support status breakdown via buildStatusChildren.
    const ec2TopChildren = () => buildInstanceChildren(top._volumes);

    if (rest.length === 0 || rest[0] === top.name) {
      top.children = ec2TopChildren();
      return { name: 'All', type: 'root', children: [top] };
    }
    const instVols = top._volumes.filter(
      (v) => (v.instanceId || '(no instance)') === rest[0]
    );
    if (instVols.length > 0) {
      const inst: HierarchyBuilderNode = {
        name: rest[0],
        type: 'instance',
        _volumes: instVols,
        value: instVols.reduce((s, v) => s + (v.size || 0), 0),
        count: instVols.length,
        children: buildStatusChildren(instVols),
      };
      return { name: 'All', type: 'root', children: [inst] };
    }
    top.children = ec2TopChildren();
    return { name: 'All', type: 'root', children: [top] };
  }

  // Unattached — status buckets, same as every other top group.
  attachStatusOnly(top);
  return { name: 'All', type: 'root', children: [top] };
}


